import './AppToolInvocationDetails.css'
import StepDuration from './StepDuration'
import { toolTargetDetails } from '../lib/toolTargetDetails'
import { useAdminMode } from '../hooks/useAdminMode'
import type { AppTool } from '../guidance/app-tools/schema'
import { useGetAppToolsQuery, useGetAppArtifactsQuery } from '../services/projectDataApi'

export default function AppToolInvocationDetails({ projectId, toolId, revision, arguments: args, snapshotTool, failure, runId, stepTimings }: {
  stepTimings?: Array<{ index: number; duration_ms?: number }>
  runId?: string; snapshotTool?: AppTool; failure?: { stepIndex: number; message: string; status: string }
  projectId: string; toolId: string; revision?: number; arguments?: Record<string, string>
}) {
  const adminMode = useAdminMode()
  const artifacts = useGetAppArtifactsQuery({ projectId, runId }, { skip: !adminMode })
  const query = useGetAppToolsQuery(projectId, { pollingInterval: 15000, skipPollingIfUnfocused: true, skip: !!snapshotTool })
  if (!snapshotTool && query.isLoading) return <p className="journey-detail__muted">Loading shared tool…</p>
  if (!snapshotTool && query.isError) return <p role="alert">Could not load this app tool.</p>
  const tool = snapshotTool ?? query.data?.tools.find(t => t.id === toolId)
  const current = tool?.revisions.find(r => r.revision === (revision ?? tool.currentRevision))
  if (!tool || !current) return <p role="alert">This tool revision is unavailable.</p>
  return <details className={`app-tool-details${failure ? ' app-tool-details--failed' : ''}`} open={failure ? true : undefined}>
    <summary>{failure && <strong>{failure.status === 'blocked' ? 'Blocked' : 'Stopped'} at step {failure.stepIndex + 1} · </strong>}{tool.displayName ?? tool.description} · {current.steps.actions.length} steps{tool.requiresAuth && ' · Requires auth'}</summary>
    <div className="app-tool-details__body">
      {args && Object.keys(args).length > 0 && <dl className="app-tool-details__fields">
        {Object.entries(args).map(([name, value]) => <div key={name}><dt>{name}</dt><dd><code>{value}</code></dd></div>)}
      </dl>}
      <ol className="app-tool-details__steps" aria-label="Tool steps">
        {current.steps.actions.map((step, index) => {
          const target = adminMode ? toolTargetDetails(artifacts.data, step.component?.surfaceId ?? step.surfaceId, step.component?.controlId ?? step.controlId) : null
          const site = target?.path[target.path.length - 1]
          const component = target?.path.length && target.path.length > 1 ? target.path[target.path.length - 2] : site
          return <li key={index} className={`app-tool-details__step${failure?.stepIndex === index ? ' app-tool-details__step--failed' : ''}`}>
          <div className="app-tool-details__step-heading">
            <span className="app-tool-details__number">{index + 1}</span>
            <p>{step.action}</p>
          </div>
          {adminMode && runId && <StepDuration durationMs={stepTimings?.find(result => result.index === index)?.duration_ms} />}
          {failure?.stepIndex === index && <p className="app-tool-details__failure" role="status">{failure.status === 'blocked' ? 'Blocked' : 'Stopped'} here: {failure.message}</p>}
          {step.value !== undefined && <dl className="app-tool-details__fields">
            <div><dt>Value</dt><dd><code>{step.value === ' ' ? 'Space' : step.value}</code></dd></div>
          </dl>}
          {adminMode && <details className="app-tool-details__technical">
            <summary>Details</summary>
            <dl className="app-tool-details__fields">
              <div><dt>Locator</dt><dd><code>{step.selector ?? 'No element locator'}</code></dd></div>
              <div><dt>Sightmap component</dt><dd>{component?.name ?? step.controlName ?? 'Name not recorded'}
                <code>{component?.id ?? step.component?.controlId ?? step.controlId ?? 'No component reference'}</code>
                {component && <code>{Array.isArray(component.selector) ? component.selector.join(' | ') : component.selector}</code>}
              </dd></div>
              <div><dt>Surface</dt><dd>{target?.surface}<code>{step.component?.surfaceId ?? step.surfaceId ?? 'Not recorded'}</code></dd></div>
              <div><dt>Interaction site</dt><dd>{site?.name ?? step.controlName ?? step.action}<code>{site?.id ?? step.component?.controlId ?? step.controlId}</code><code>{step.type}{step.keyboardEvent ? ` (${step.keyboardEvent})` : ''}</code></dd></div>
              {step.checks.map((check, i) => typeof check !== 'string' && check.selector && <div key={i}><dt>Check {i + 1} locator</dt><dd><code>{check.selector}</code></dd></div>)}
            </dl>
          </details>}
          <div className="app-tool-details__checks">
            <p className="app-tool-details__checks-label">Checks</p>
            {step.checks.length === 0 ? <p className="app-tool-details__empty">No checks</p> : <ul>
              {step.checks.map((check, i) => <li key={i}>
                <p>{typeof check === 'string' ? check : check.description}</p>
                {typeof check !== 'string' && check.expected !== undefined && <dl className="app-tool-details__fields">
                  {check.expected !== undefined && <div><dt>Expected</dt><dd><code>{check.expected}</code></dd></div>}
                </dl>}
              </li>)}
            </ul>}
          </div>
        </li>})}
      </ol>
    </div>
  </details>
}
